import { publicHttpClient } from '@ahm/api-wrappers-core';
import { ensureValidToken, triggerRefresh } from './client-refresh';
import { getAuthToken } from './get-token';
import { clearAuthCache, useNewAuthStore } from '@/store/use-new-auth-store';

let installed = false;

const AHAFOOD_API_HOST = (() => {
  try {
    return new URL(
      process.env.NEXT_PUBLIC_API_URI ?? 'https://apistg.ahamove.com'
    ).host;
  } catch {
    return '';
  }
})();

/**
 * Check if request URL targets AhaFood BE host. Skip Bearer attach cho
 * cross-origin URL (CMS strapi, dplaces, google geocode...) vì các BE đó
 * không expect Ahamove JWT → trả 401.
 */
function shouldAttachBearer(url: string | undefined): boolean {
  if (!url) return true;
  // Relative URL → assume API_URI baseURL → attach
  if (url.startsWith('/') || !/^https?:\/\//.test(url)) return true;
  // Absolute URL → only attach nếu match AhaFood host
  try {
    return new URL(url).host === AHAFOOD_API_HOST;
  } catch {
    return false;
  }
}

/**
 * 2-tier refresh wrapper (tier 1 pre-emptive + tier 2 reactive).
 * Doc reference: refesh-token.md §4 Trigger points.
 *
 * Request interceptor:
 *  - Tier 1: ensureValidToken — nếu exp < 30s, refresh trước, sau đó attach Bearer
 *  - Idempotent: không override Authorization header tự set
 *
 * Response interceptor:
 *  - Tier 2: 401 + chưa retry → triggerRefresh → retry request 1 lần
 *  - Refresh fail → clear cookies + store + cache → propagate 401
 */
export function installAuthAxiosInterceptor() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  publicHttpClient.interceptors.request.use(async (config) => {
    if (
      config.headers &&
      !config.headers.Authorization &&
      shouldAttachBearer(config.url)
    ) {
      const token = await ensureValidToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  publicHttpClient.interceptors.response.use(
    (res) => res,
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config;
      if (
        status !== 401 ||
        !original ||
        original._retry ||
        !shouldAttachBearer(original.url)
      ) {
        return Promise.reject(error);
      }
      original._retry = true;

      const ok = await triggerRefresh();
      if (!ok) {
        // Refresh fail (refresh_token cũng invalid/missing) → terminal logout
        terminalLogout();
        return Promise.reject(error);
      }

      const newToken = getAuthToken();
      if (newToken && original.headers) {
        original.headers.Authorization = `Bearer ${newToken}`;
      }
      return publicHttpClient.request(original);
    }
  );
}

/**
 * Clear toàn bộ auth state khi refresh chain fail.
 * Tránh UI hiện "đang login" trong khi mọi API trả 401.
 */
function terminalLogout() {
  try {
    useNewAuthStore.getState().logout();
    clearAuthCache();
    void fetch('/api/auth/logout', { method: 'POST' });
  } catch {
    /* ignore — best-effort cleanup */
  }
}
